// features/customers/CustomersPage.tsx
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { SettingsModal } from '@/features/settings/components/SettingsModal';
import apiClient from '@/lib/api-client';
import {
    Users, Search, X, Save, Phone, Mail,
    CreditCard, RefreshCw, UserPlus, DollarSign,
    Award, LayoutGrid, List, Upload, Download, FileSpreadsheet, CheckCircle2, AlertCircle,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { clsx } from 'clsx';

interface Customer {
    id: string; firstName: string; lastName: string; email: string | null;
    phone: string; totalSpent: string; visitCount: string;
    storeCredit: string; debtBalance: string; loyaltyPoints: string;
    loyaltyTier: string; tags: string[]; isActive: boolean;
    createdAt: string;
}

type ViewMode = 'card' | 'table';

const TIER_STYLES: Record<string, { bg: string; text: string; border: string; icon: React.ElementType }> = {
    bronze: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', icon: Award },
    silver: { bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-200', icon: Award },
    gold: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200', icon: Award },
    platinum: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', icon: Award },
};

export function CustomersPage() {
    const user = useAuthStore((s) => s.user);
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);

    const [viewMode, setViewMode] = useState<ViewMode>('card');
    const [search, setSearch] = useState('');
    const [tierFilter, setTierFilter] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [showBulkModal, setShowBulkModal] = useState(false);
    const [form, setForm] = useState({ firstName: '', lastName: '', phone: '', email: '', address: '', dateOfBirth: '' });

    const { data: customersData, isLoading, isError, refetch } = useQuery({
        queryKey: ['customers', user?.tenantId, search, tierFilter],
        queryFn: async () => {
            const params: any = { limit: 50, sortBy: 'createdAt', order: 'desc' };
            if (search) params.search = search;
            if (tierFilter) params.loyaltyTier = tierFilter;
            const { data } = await apiClient.get(`/tenants/${user!.tenantId}/customers`, { params });
            return { data: data?.data || [] };
        },
        enabled: !!user?.tenantId,
    });

    const createMutation = useMutation({
        mutationFn: (payload: any) => apiClient.post(`/tenants/${user!.tenantId}/customers`, payload),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['customers'] });
            setShowForm(false);
            setForm({ firstName: '', lastName: '', phone: '', email: '', address: '', dateOfBirth: '' });
            addToast({ type: 'success', title: 'Customer created' });
        },
        onError: (err: any) => addToast({ type: 'error', title: 'Failed', message: err?.response?.data?.error?.message }),
    });

    const formatNaira = (val: string) => {
        const num = parseFloat(val || '0');
        return isNaN(num) ? '₦0.00' : `₦${num.toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
    };

    const customers = customersData?.data || [];
    const totalCustomers = customers.length;
    const totalSpent = customers.reduce((sum: number, c: Customer) => sum + parseFloat(c.totalSpent || '0'), 0);
    const withDebt = customers.filter((c: Customer) => parseFloat(c.debtBalance) > 0).length;
    const platinumCount = customers.filter((c: Customer) => c.loyaltyTier === 'platinum' || c.loyaltyTier === 'gold').length;

    const hasActiveFilters = search || tierFilter;

    if (isLoading) return <div className="flex justify-center py-20"><Spinner size="xl" /></div>;
    if (isError) return <ErrorState title="Failed to load customers" onRetry={() => refetch()} />;

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30">
            {/* Premium Header */}
            <div className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 shadow-sm">
                <div className="max-w-[1600px] mx-auto px-6 lg:px-8 py-4">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div className="flex items-center gap-4">
                            <div className="p-2.5 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-lg shadow-blue-500/25">
                                <Users className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-slate-900">Customers</h1>
                                <p className="text-sm text-slate-500">
                                    {totalCustomers} customers · {platinumCount} VIP · ₦{totalSpent.toLocaleString()} total spent
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button onClick={() => refetch()} className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-all text-sm font-medium text-slate-700 shadow-sm" type="button">
                                <RefreshCw className="w-4 h-4" /> Refresh
                            </button>
                            <div className="flex items-center bg-slate-100 rounded-xl p-0.5">
                                {[{ mode: 'card' as ViewMode, icon: LayoutGrid }, { mode: 'table' as ViewMode, icon: List }].map(({ mode, icon: Icon }) => (
                                    <button key={mode} onClick={() => setViewMode(mode)} className={clsx('p-1.5 rounded-lg transition-all', viewMode === mode ? 'bg-white shadow-sm text-blue-600' : 'text-slate-400')} type="button"><Icon size={16} /></button>
                                ))}
                            </div>
                            <button onClick={() => setShowBulkModal(true)} className="flex items-center gap-2 px-4 py-2.5 bg-white border border-blue-200 text-blue-700 rounded-xl hover:bg-blue-50 transition-all font-semibold text-sm shadow-sm" type="button">
                                <Upload className="w-4 h-4" /> Bulk Upload
                            </button>
                            <button onClick={() => setShowForm(true)} className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl hover:from-blue-600 hover:to-indigo-700 transition-all font-semibold shadow-lg shadow-blue-500/25 text-sm" type="button">
                                <UserPlus className="w-4 h-4" /> Add Customer
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-[1600px] mx-auto p-6 lg:p-8 space-y-6">
                {/* Stats */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <StatCard label="Total Customers" value={totalCustomers} icon={Users} gradient="from-blue-500 to-indigo-600" />
                    <StatCard label="VIP Customers" value={platinumCount} icon={Award} gradient="from-purple-500 to-violet-600" />
                    <StatCard label="Total Spent" value={formatNaira(String(totalSpent))} icon={DollarSign} gradient="from-emerald-500 to-teal-600" />
                    <StatCard label="With Debt" value={withDebt} icon={CreditCard} gradient="from-rose-500 to-pink-600" warning={withDebt > 0} />
                </div>

                {/* Search & Filters */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-1.5">
                    <div className="flex flex-col lg:flex-row gap-1.5">
                        <div className="relative flex-1">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                            <input type="text" placeholder="Search by name or phone..." value={search} onChange={(e) => setSearch(e.target.value)}
                                className="w-full pl-12 pr-12 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 focus:bg-white transition-all text-sm" />
                            {search && <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400" type="button"><X className="w-4 h-4" /></button>}
                        </div>
                        <select value={tierFilter} onChange={(e) => setTierFilter(e.target.value)} className="px-4 py-3 border-2 border-slate-200 rounded-xl text-sm bg-white">
                            <option value="">All Tiers</option>
                            <option value="bronze">Bronze</option>
                            <option value="silver">Silver</option>
                            <option value="gold">Gold</option>
                            <option value="platinum">Platinum</option>
                        </select>
                        {hasActiveFilters && <button onClick={() => { setSearch(''); setTierFilter(''); }} className="text-sm text-blue-600 font-medium px-2" type="button">Clear</button>}
                    </div>
                </div>

                {/* Content */}
                {customers.length === 0 ? (
                    <EmptyState icon={<Users className="w-16 h-16" />} title={hasActiveFilters ? "No matching customers" : "No customers yet"} description={hasActiveFilters ? "Try adjusting your search." : "Add your first customer."} action={!hasActiveFilters ? { label: 'Add First Customer', onClick: () => setShowForm(true) } : undefined} />
                ) : viewMode === 'card' ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {customers.map((customer: Customer) => {
                            const tier = TIER_STYLES[customer.loyaltyTier] || TIER_STYLES.bronze;
                            const TierIcon = tier.icon;
                            return (
                                <motion.div key={customer.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} whileHover={{ y: -4 }}
                                    onClick={() => navigate(`/customers/${customer.id}`)}
                                    className="group bg-white rounded-2xl border-2 border-slate-200 shadow-sm hover:shadow-xl hover:border-blue-300 transition-all duration-300 cursor-pointer overflow-hidden">
                                    <div className="h-1.5 bg-gradient-to-r from-blue-500 to-indigo-600" />
                                    <div className="p-5">
                                        <div className="flex items-start justify-between mb-3">
                                            <div className="flex items-center gap-3">
                                                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-lg shadow-md">
                                                    {customer.firstName?.[0]}{customer.lastName?.[0]}
                                                </div>
                                                <div>
                                                    <h3 className="font-semibold text-slate-900">{customer.firstName} {customer.lastName}</h3>
                                                    <div className="flex items-center gap-2 mt-0.5">
                                                        <span className={clsx('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border', tier.bg, tier.text, tier.border)}>
                                                            <TierIcon size={10} />{customer.loyaltyTier}
                                                        </span>
                                                        <span className="text-xs text-slate-400">{customer.loyaltyPoints} pts</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <Badge variant={customer.isActive ? 'success' : 'danger'} size="sm">{customer.isActive ? 'Active' : 'Inactive'}</Badge>
                                        </div>
                                        <div className="space-y-1.5 text-sm text-slate-500">
                                            <div className="flex items-center gap-2"><Phone size={13} />{customer.phone}</div>
                                            {customer.email && <div className="flex items-center gap-2 truncate"><Mail size={13} />{customer.email}</div>}
                                        </div>
                                    </div>
                                    <div className="px-5 py-3 bg-slate-50 border-t border-slate-100 grid grid-cols-3 gap-2 text-center">
                                        <div><p className="text-[10px] text-slate-400">Spent</p><p className="text-sm font-bold text-slate-700">{formatNaira(customer.totalSpent)}</p></div>
                                        <div><p className="text-[10px] text-slate-400">Credit</p><p className="text-sm font-bold text-emerald-600">{formatNaira(customer.storeCredit)}</p></div>
                                        <div><p className="text-[10px] text-slate-400">Debt</p><p className={clsx('text-sm font-bold', parseFloat(customer.debtBalance) > 0 ? 'text-rose-600' : 'text-slate-400')}>{formatNaira(customer.debtBalance)}</p></div>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                ) : (
                    <Card className="overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-slate-100">
                                <thead><tr className="bg-slate-50/50">
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Customer</th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">Contact</th>
                                    <th className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">Tier</th>
                                    <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Total Spent</th>
                                    <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Store Credit</th>
                                    <th className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                                </tr></thead>
                                <tbody className="divide-y divide-slate-50">
                                    {customers.map((customer: Customer) => {
                                        const tier = TIER_STYLES[customer.loyaltyTier] || TIER_STYLES.bronze;
                                        return (
                                            <tr key={customer.id} onClick={() => navigate(`/customers/${customer.id}`)} className="group hover:bg-slate-50 cursor-pointer transition-colors">
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-sm">{customer.firstName?.[0]}{customer.lastName?.[0]}</div>
                                                        <div><p className="text-sm font-medium text-slate-900">{customer.firstName} {customer.lastName}</p><p className="text-xs text-slate-400">{customer.loyaltyPoints} pts</p></div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 hidden md:table-cell text-sm text-slate-500">{customer.phone}</td>
                                                <td className="px-6 py-4 text-center"><span className={clsx('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border', tier.bg, tier.text, tier.border)}><tier.icon size={10} />{customer.loyaltyTier}</span></td>
                                                <td className="px-6 py-4 text-right text-sm font-semibold">{formatNaira(customer.totalSpent)}</td>
                                                <td className="px-6 py-4 text-right text-sm text-emerald-600 font-medium">{formatNaira(customer.storeCredit)}</td>
                                                <td className="px-6 py-4 text-center"><Badge variant={customer.isActive ? 'success' : 'danger'} size="sm">{customer.isActive ? 'Active' : 'Inactive'}</Badge></td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </Card>
                )}

                {/* Add Customer Modal */}
                <SettingsModal open={showForm} onClose={() => setShowForm(false)} title="Add Customer" subtitle="Create a new customer record" icon={UserPlus}
                    footer={<><Button variant="secondary" onClick={() => setShowForm(false)}>Cancel</Button>
                        <Button onClick={() => createMutation.mutate(form)} isLoading={createMutation.isPending} disabled={!form.firstName || !form.phone}>
                            <Save size={16} className="mr-1.5" /> Save Customer</Button></>}>
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                            <Input label="First Name *" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} required />
                            <Input label="Last Name *" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} required />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <Input label="Phone *" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} leftIcon={<Phone size={14} />} required />
                            <Input label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} leftIcon={<Mail size={14} />} />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <Input label="Date of Birth" type="date" value={form.dateOfBirth} onChange={(e) => setForm({ ...form, dateOfBirth: e.target.value })} />
                            <Input label="Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Residential or delivery address" />
                        </div>
                    </div>
                </SettingsModal>

                {/* Bulk Customer Upload Modal */}
                {showBulkModal && user?.tenantId && (
                    <CustomerBulkUploadModal
                        isOpen={showBulkModal}
                        onClose={() => setShowBulkModal(false)}
                        tenantId={user.tenantId}
                        onSuccess={() => {
                            queryClient.invalidateQueries({ queryKey: ['customers'] });
                            setShowBulkModal(false);
                            addToast({ type: 'success', title: 'Customers imported successfully' });
                        }}
                    />
                )}
            </div>
        </div>
    );
}

// ============================================================
// CUSTOMER BULK UPLOAD MODAL
// ============================================================

interface CustomerBulkUploadModalProps {
    isOpen: boolean;
    onClose: () => void;
    tenantId: string;
    onSuccess: () => void;
}

function CustomerBulkUploadModal({ isOpen, onClose, tenantId, onSuccess }: CustomerBulkUploadModalProps) {
    const addToast = useToastStore((s) => s.addToast);
    const [file, setFile] = useState<File | null>(null);
    const [parsedRows, setParsedRows] = useState<any[]>([]);
    const [branchId, setBranchId] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Query branches for branch scoping
    const { data: branches } = useQuery({
        queryKey: ['branches-customer-bulk', tenantId],
        queryFn: async () => {
            try {
                const res = await apiClient.get(`/tenants/${tenantId}/branches`);
                return res.data?.data || res.data || [];
            } catch {
                return [];
            }
        },
        enabled: isOpen && !!tenantId,
    });

    if (!isOpen) return null;

    const downloadTemplate = () => {
        const csv = 'First Name,Last Name,Phone,Email,Address,Customer Type\nJohn,Doe,08012345678,john@example.com,Lagos Nigeria,retail\nJane,Smith,08098765432,jane@example.com,Abuja Nigeria,vip\n';
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', 'etipos_customers_template.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selected = e.target.files?.[0];
        if (!selected) return;
        setFile(selected);

        const reader = new FileReader();
        reader.onload = (evt) => {
            const text = evt.target?.result as string;
            if (!text) return;

            const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
            if (lines.length <= 1) {
                addToast({ type: 'warning', title: 'File is empty', message: 'No customer records found in file.' });
                return;
            }

            const rows: any[] = [];
            // Parse headers
            const headers = lines[0].split(',').map((h) => h.trim().toLowerCase().replace(/[^a-z0-9]/g, ''));

            for (let i = 1; i < lines.length; i++) {
                const parts = lines[i].split(',').map((p) => p.trim().replace(/^["']|["']$/g, ''));
                if (parts.length < 2) continue;

                const rowObj: any = {};
                headers.forEach((h, idx) => {
                    if (h.includes('first')) rowObj.firstName = parts[idx];
                    else if (h.includes('last')) rowObj.lastName = parts[idx];
                    else if (h.includes('phone')) rowObj.phone = parts[idx];
                    else if (h.includes('mail')) rowObj.email = parts[idx];
                    else if (h.includes('address')) rowObj.address = parts[idx];
                    else if (h.includes('type')) rowObj.customerType = parts[idx];
                });

                if (rowObj.firstName && rowObj.phone) {
                    rows.push({
                        firstName: rowObj.firstName,
                        lastName: rowObj.lastName || '',
                        phone: rowObj.phone,
                        email: rowObj.email || undefined,
                        address: rowObj.address || undefined,
                        customerType: (rowObj.customerType || 'retail').toLowerCase(),
                    });
                }
            }

            setParsedRows(rows);
            if (rows.length === 0) {
                addToast({ type: 'error', title: 'Invalid format', message: 'Could not find required First Name and Phone columns.' });
            }
        };
        reader.readAsText(selected);
    };

    const handleImport = async () => {
        if (parsedRows.length === 0) return;
        setIsSubmitting(true);
        try {
            await apiClient.post(`/tenants/${tenantId}/customers/bulk`, {
                defaultBranchId: branchId || undefined,
                customers: parsedRows,
            });
            onSuccess();
        } catch (err: any) {
            addToast({
                type: 'error',
                title: 'Import failed',
                message: err?.response?.data?.error?.message || 'Could not import customers. Check your data.',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white rounded-3xl shadow-2xl border border-slate-200 w-full max-w-xl max-h-[85vh] flex flex-col overflow-hidden"
            >
                {/* Header */}
                <div className="p-5 border-b border-slate-100 flex items-center justify-between bg-slate-50">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 rounded-xl bg-blue-100 text-blue-700">
                            <Upload className="w-5 h-5" />
                        </div>
                        <div>
                            <h3 className="font-bold text-slate-900 text-base">Bulk Customer Upload</h3>
                            <p className="text-xs text-slate-500">Import customers via CSV spreadsheet</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-xl hover:bg-slate-200 text-slate-400">
                        <X size={18} />
                    </button>
                </div>

                {/* Body */}
                <div className="p-6 space-y-4 overflow-y-auto flex-1">
                    {/* Template Downloader */}
                    <div className="p-4 bg-blue-50/60 rounded-2xl border border-blue-200 flex items-center justify-between">
                        <div>
                            <p className="text-xs font-bold text-blue-900">Need the CSV Template?</p>
                            <p className="text-[11px] text-blue-600">Download formatted spreadsheet with sample customer rows</p>
                        </div>
                        <button
                            type="button"
                            onClick={downloadTemplate}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-xl transition-colors shadow-xs"
                        >
                            <Download size={13} /> Template
                        </button>
                    </div>

                    {/* Branch Assignment */}
                    <div>
                        <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                            Assign to Branch (Branch Enforcement)
                        </label>
                        <select
                            value={branchId}
                            onChange={(e) => setBranchId(e.target.value)}
                            className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">All Branches / Global</option>
                            {(branches || []).map((b: any) => (
                                <option key={b.id} value={b.id}>{b.name}</option>
                            ))}
                        </select>
                        <p className="text-[10px] text-slate-400 mt-1">
                            Scopes customers to this branch so staff from unrelated branches cannot access their data.
                        </p>
                    </div>

                    {/* File Picker */}
                    <div className="border-2 border-dashed border-slate-200 rounded-2xl p-6 text-center hover:border-blue-400 transition-colors">
                        <FileSpreadsheet className="w-10 h-10 text-slate-400 mx-auto mb-2" />
                        <label className="cursor-pointer">
                            <span className="text-xs font-bold text-blue-600 hover:underline">Click to upload CSV</span>
                            <span className="text-xs text-slate-500"> or drag and drop</span>
                            <input
                                type="file"
                                accept=".csv"
                                onChange={handleFileChange}
                                className="hidden"
                            />
                        </label>
                        {file && (
                            <p className="text-xs font-semibold text-slate-800 mt-2">
                                Selected: <span className="text-blue-700">{file.name}</span>
                            </p>
                        )}
                    </div>

                    {/* Parsed Preview */}
                    {parsedRows.length > 0 && (
                        <div className="space-y-2">
                            <div className="flex items-center justify-between text-xs">
                                <span className="font-bold text-slate-800">Preview ({parsedRows.length} valid customers found)</span>
                                <span className="text-emerald-600 font-semibold flex items-center gap-1">
                                    <CheckCircle2 size={12} /> Ready
                                </span>
                            </div>
                            <div className="max-h-36 overflow-y-auto border border-slate-200 rounded-xl divide-y divide-slate-100 text-xs">
                                {parsedRows.slice(0, 5).map((r, i) => (
                                    <div key={i} className="p-2 flex items-center justify-between">
                                        <div>
                                            <span className="font-semibold text-slate-800">{r.firstName} {r.lastName}</span>
                                            <span className="text-slate-400 ml-2">{r.phone}</span>
                                        </div>
                                        <span className="text-[10px] font-mono text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                                            {r.customerType}
                                        </span>
                                    </div>
                                ))}
                                {parsedRows.length > 5 && (
                                    <div className="p-2 text-center text-[11px] text-slate-400">
                                        ...and {parsedRows.length - 5} more customers
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-slate-100 flex items-center justify-between bg-slate-50">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-200 rounded-xl transition-colors"
                    >
                        Cancel
                    </button>
                    <Button
                        onClick={handleImport}
                        disabled={parsedRows.length === 0 || isSubmitting}
                        isLoading={isSubmitting}
                        className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs rounded-xl"
                    >
                        Import {parsedRows.length} Customers
                    </Button>
                </div>
            </motion.div>
        </div>
    );
}

const StatCard: React.FC<{ label: string; value: string | number; icon: React.ElementType; gradient: string; warning?: boolean }> = ({ label, value, icon: Icon, gradient, warning }) => (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} whileHover={{ y: -3 }}
        className={clsx('bg-white rounded-2xl border shadow-sm p-5 hover:shadow-lg transition-all duration-300', warning ? 'border-rose-200' : 'border-slate-200')}>
        <div className="flex items-start justify-between">
            <div className="space-y-1"><p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</p><p className="text-3xl font-bold text-slate-900 mt-1">{value}</p></div>
            <div className={`p-3 rounded-xl bg-gradient-to-br ${gradient} shadow-lg`}><Icon className="w-5 h-5 text-white" /></div>
        </div>
    </motion.div>
);